package com.trevorwiebe.timesheet.invoicing.presentation

import com.trevorwiebe.timesheet.invoicing.domain.model.Payment
import com.trevorwiebe.timesheet.invoicing.domain.paymentProblem
import com.trevorwiebe.timesheet.invoicing.domain.openInvoices
import com.trevorwiebe.timesheet.core.domain.dollarsToCents
import com.trevorwiebe.timesheet.core.domain.formatCentsAsDollars
import com.trevorwiebe.timesheet.core.domain.model.Customer
import com.trevorwiebe.timesheet.invoicing.domain.invoiceableLoads
import com.trevorwiebe.timesheet.invoicing.domain.invoicedLoadIds
import com.trevorwiebe.timesheet.invoicing.domain.isBillable
import com.trevorwiebe.timesheet.invoicing.domain.refreshedInvoiceLines
import com.trevorwiebe.timesheet.invoicing.domain.model.Invoice
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceLine
import com.trevorwiebe.timesheet.invoicing.domain.model.InvoiceStatus
import com.trevorwiebe.timesheet.invoicing.domain.model.PaymentState
import com.trevorwiebe.timesheet.invoicing.domain.toInvoiceLine
import com.trevorwiebe.timesheet.loads.domain.model.Load
import com.trevorwiebe.timesheet.loads.domain.model.LoadAttachment
import com.trevorwiebe.timesheet.mileage.domain.model.Location
import kotlinx.datetime.LocalDate

enum class InvoiceListTab(val label: String) { DRAFTS("Drafts"), OPEN("Open"), PAID("Paid") }

/** A load offered on the new-invoice screen, with the line it would become. */
data class InvoiceableLoad(
    val load: Load,
    val line: InvoiceLine,
    val isBillable: Boolean,
)

data class NewInvoiceState(
    val customerId: String = "",
    // Null means every outstanding load, however old.
    val startDate: LocalDate? = null,
    val endDate: LocalDate,
    val selectedLoadIds: Set<String> = emptySet(),
    val showStartPicker: Boolean = false,
    val showEndPicker: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
)

data class ReceivePaymentState(
    val customerId: String = "",
    val amountText: String = "",
    val date: LocalDate,
    val allocationTexts: Map<String, String> = emptyMap(),
    // Once an invoice's amount is typed by hand, changing the payment total no longer refills them.
    val allocationsEdited: Boolean = false,
    val showDatePicker: Boolean = false,
    val isSaving: Boolean = false,
    val error: String? = null,
)

data class SendInvoiceState(
    // Comma-separated; starts as the customer's saved addresses.
    val to: String,
    val documentUrls: Set<String> = emptySet(),
    val isSending: Boolean = false,
    val error: String? = null,
)

/** The files on one invoiced load, any of which can go out with the invoice email. */
data class InvoiceLoadDocuments(val line: InvoiceLine, val attachments: List<LoadAttachment>)

fun parseRecipients(text: String): List<String> =
    text.split(',', ';', ' ', '\n').map { it.trim() }.filter { it.isNotEmpty() }

/** A payment as it applies to one invoice. */
data class InvoicePaymentRow(val payment: Payment, val applied: Int) {
    val appliedFormatted: String get() = "$" + applied.formatCentsAsDollars()
}

data class InvoicingState(
    val today: LocalDate,
    val invoices: List<Invoice> = emptyList(),
    val customers: List<Customer> = emptyList(),
    val loads: List<Load> = emptyList(),
    val locations: List<Location> = emptyList(),
    val isLoading: Boolean = true,
    val error: String? = null,
    val tab: InvoiceListTab = InvoiceListTab.DRAFTS,
    val newInvoice: NewInvoiceState? = null,
    val selectedInvoiceId: String? = null,
    val showDeleteConfirm: Boolean = false,
    val isDeleting: Boolean = false,
    val payments: List<Payment> = emptyList(),
    val receivePayment: ReceivePaymentState? = null,
    val showMarkSentConfirm: Boolean = false,
    val isMarkingSent: Boolean = false,
    val paymentToDelete: Payment? = null,
    val isDeletingPayment: Boolean = false,
    val isGeneratingPdf: Boolean = false,
    // Set when a PDF link is ready; the UI opens it and reports OnPdfOpened.
    val pdfUrlToOpen: String? = null,
    val sendDialog: SendInvoiceState? = null,
    // The document open in the viewer, and downloaded document PDFs by url.
    val openDocumentUrl: String? = null,
    val documentPdfs: Map<String, ByteArray> = emptyMap(),
    val infoTitle: String = "",
    val infoMessage: String? = null,
    val showUpdateConfirm: Boolean = false,
    val isUpdatingInvoice: Boolean = false,
) {
    val visibleInvoices: List<Invoice>
        get() = invoices.filter { invoice ->
            when (tab) {
                InvoiceListTab.DRAFTS -> invoice.status == InvoiceStatus.DRAFT
                InvoiceListTab.OPEN -> invoice.status == InvoiceStatus.SENT && invoice.paymentState != PaymentState.PAID
                InvoiceListTab.PAID -> invoice.status == InvoiceStatus.SENT && invoice.paymentState == PaymentState.PAID
            }
        }

    val selectedInvoice: Invoice? get() = invoices.firstOrNull { it.id == selectedInvoiceId }

    /** The selected invoice's lines as its loads read now. */
    val selectedInvoiceCurrentLines: List<InvoiceLine>
        get() = selectedInvoice?.let { refreshedInvoiceLines(it, loads, locations) } ?: emptyList()

    /** A sent invoice whose loads were edited after it was sent, so it can be updated to match. */
    val selectedInvoiceIsOutdated: Boolean
        get() = selectedInvoice?.let { !it.isDraft && selectedInvoiceCurrentLines != it.lines } == true

    fun loadFor(line: InvoiceLine): Load? = loads.firstOrNull { it.id == line.loadId }

    val openDocument: LoadAttachment?
        get() = openDocumentUrl?.let { url -> selectedInvoiceDocuments.flatMap { it.attachments }.firstOrNull { it.url == url } }

    val selectedInvoiceDocuments: List<InvoiceLoadDocuments>
        get() {
            val invoice = selectedInvoice ?: return emptyList()
            val byId = loads.associateBy { it.id }
            return invoice.lines.mapNotNull { line ->
                val attachments = byId[line.loadId]?.attachments.orEmpty()
                if (attachments.isEmpty()) null else InvoiceLoadDocuments(line, attachments)
            }
        }

    val sortedCustomers: List<Customer>
        get() = customers.filter { !it.deleted }.sortedBy { customerName(it).lowercase() }

    fun customerName(customer: Customer): String = customer.contactName.ifBlank { customer.companyName }

    /** Uninvoiced, billable loads per customer, for the counts beside each customer. */
    val outstandingByCustomer: Map<String, Int>
        get() {
            val invoiced = invoicedLoadIds(invoices)
            return loads
                .filter { it.id !in invoiced && it.isBillable && (it.scheduledDate?.let { d -> d <= today } == true) }
                .groupingBy { it.customerId }
                .eachCount()
        }

    val invoiceableLoads: List<InvoiceableLoad>
        get() {
            val form = newInvoice ?: return emptyList()
            if (form.customerId.isEmpty()) return emptyList()
            return invoiceableLoads(loads, invoicedLoadIds(invoices), form.customerId, form.startDate, form.endDate, today)
                .map { InvoiceableLoad(it, it.toInvoiceLine(locations), it.isBillable) }
        }

    val newInvoiceTotal: Int
        get() {
            val selected = newInvoice?.selectedLoadIds ?: return 0
            return invoiceableLoads.filter { it.load.id in selected }.sumOf { it.line.total }
        }

    val newInvoiceTotalFormatted: String get() = "$" + newInvoiceTotal.formatCentsAsDollars()

    val unbillableNotice: String?
        get() {
            val count = invoiceableLoads.count { !it.isBillable }
            if (count == 0) return null
            return if (count == 1) {
                "1 load needs a rate before it can be invoiced. Tap a load to add its rate."
            } else {
                "$count loads need a rate before they can be invoiced. Tap a load to add its rate."
            }
        }

    val canCreateInvoice: Boolean
        get() = newInvoice?.let { !it.isSaving && it.selectedLoadIds.isNotEmpty() } == true

    // ── Payments ──────────────────────────────────────────────────────────

    val selectedInvoicePayments: List<InvoicePaymentRow>
        get() {
            val id = selectedInvoiceId ?: return emptyList()
            return payments
                .filter { payment -> payment.allocations.any { it.invoiceId == id } }
                .map { InvoicePaymentRow(it, it.amountAppliedTo(id)) }
        }

    /** "2 open • $3,400.00 due" per customer with unpaid sent invoices, for the payment customer list. */
    val openSummaryByCustomer: Map<String, String>
        get() = invoices
            .filter { it.status == InvoiceStatus.SENT && it.balance > 0 }
            .groupBy { it.customerId }
            .mapValues { (_, open) ->
                "${open.size} open • $${open.sumOf { it.balance }.formatCentsAsDollars()} due"
            }

    val paymentOpenInvoices: List<Invoice>
        get() = receivePayment?.let { openInvoices(invoices, it.customerId) } ?: emptyList()

    val paymentAmount: Int get() = receivePayment?.amountText?.dollarsToCents() ?: 0

    val paymentAllocations: Map<String, Int>
        get() {
            val texts = receivePayment?.allocationTexts ?: return emptyMap()
            return paymentOpenInvoices.associate { it.id to (texts[it.id]?.dollarsToCents() ?: 0) }
        }

    val paymentUnapplied: Int get() = paymentAmount - paymentAllocations.values.sum()

    val paymentUnappliedFormatted: String get() = "$" + paymentUnapplied.formatCentsAsDollars()

    /** Why the payment can't be saved yet; null once it balances. */
    val paymentProblem: String?
        get() {
            val form = receivePayment ?: return null
            if (form.customerId.isEmpty()) return "Choose who the payment is from."
            if (paymentOpenInvoices.isEmpty()) return "This customer has no sent invoices with a balance."
            return paymentProblem(paymentAmount, paymentAllocations, paymentOpenInvoices)
        }

    val canSavePayment: Boolean
        get() = receivePayment?.isSaving == false && paymentProblem == null
}
